import { Outlet } from 'react-router-dom';
import { Loading } from '@carbon/react';

import Container from 'Components/Common/Container/Container';
import { useGetUserDataQuery } from 'api/accountEndpoints';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import { useEffect, useState } from 'react';
import { api } from 'api/api';
import { useDispatch } from 'react-redux';
import {
    OPEN_JOIN_EXISTING_PARISH_EVENT,
    OPEN_INITIAL_SETUP_EVENT,
} from 'Screens/Dashboard/InitialSetup/constants';
import { useStorage } from 'hooks/useStorage';
import InitialSetupModal from './InitialSetup/InitialSetupModal';
import styles from './Dashboard.module.scss';
import AddEntity from 'Screens/Dashboard/AllEntities/AddEntity/AddEntity';
import { AddEntityProvider } from 'Screens/Dashboard/AllEntities/EntityProvider/AddEntityProvider';
import { OPEN_ADD_PARISH_EVENT } from 'Screens/Dashboard/AllEntities/constants';
import NotConfiguredEmptyState from 'Components/Common/NotConfiguredEmptyState/NotConfiguredEmptyState';
import InitialSetupChoiceModal from './InitialSetup/InitialSetupChoiceModal';
import JoinExistingParishModal from './InitialSetup/JoinExistingParishModal';

const INITIAL_SETUP_AUTO_OPEN_DISMISSED_STORAGE_KEY: string = 'todai-app.initial-setup.auto-open-dismissed';

const Dashboard = () => {
    const { data: user, isLoading, isError, refetch } = useGetUserDataQuery();
    const dispatch = useDispatch();
    const [isInitialSetupOpen, setInitialSetupOpen] = useState(false);
    const [isInitialSetupChoiceOpen, setInitialSetupChoiceOpen] = useState(false);
    const [isJoinExistingParishOpen, setJoinExistingParishOpen] = useState(false);
    const [isAddParishOpen, setAddParishOpen] = useState(false);
    const { specifiedGetItem, specifiedSetItem, specifiedRemoveItem } = useStorage('local', user?.id ?? '');
    const hasAnyEntity = Boolean(user?.entities.length);
    const isInitialSetupRequired = Boolean(user && !user.hasInitialSetupCompleted);
    const isParishRequired = Boolean(user?.hasInitialSetupCompleted && !hasAnyEntity);

    useEffect(() => {
        // Hack to reset RTK Query state on login screen load
        // Once there's more time, implement a proper solution for this
        // The issue is that after logging out, the cached data remains,
        // because the rest api retrieves id from token
        // we don't provide it in many requests so rtk query cannot differentiate between users
        dispatch(api.util.resetApiState());
    }, [dispatch]);

    useEffect(() => {
        if (!user) {
            return;
        }

        if (!isInitialSetupRequired) {
            specifiedRemoveItem(INITIAL_SETUP_AUTO_OPEN_DISMISSED_STORAGE_KEY);
            setInitialSetupOpen(false);
            setInitialSetupChoiceOpen(false);
            return;
        }

        const isAutoOpenDismissed = specifiedGetItem(
            INITIAL_SETUP_AUTO_OPEN_DISMISSED_STORAGE_KEY,
            false,
        );

        setInitialSetupChoiceOpen(!isAutoOpenDismissed);
        setInitialSetupOpen(false);
    }, [isInitialSetupRequired, specifiedGetItem, specifiedRemoveItem, user]);

    useEffect(() => {
        const openInitialSetup = () => {
            setInitialSetupChoiceOpen(false);
            setInitialSetupOpen(true);
        };
        const openJoinExistingParish = () => {
            setInitialSetupChoiceOpen(false);
            setJoinExistingParishOpen(true);
        };
        const openAddParish = () => {
            setAddParishOpen(true);
        };

        window.addEventListener(OPEN_INITIAL_SETUP_EVENT, openInitialSetup);
        window.addEventListener(OPEN_JOIN_EXISTING_PARISH_EVENT, openJoinExistingParish);
        window.addEventListener(OPEN_ADD_PARISH_EVENT, openAddParish);

        return () => {
            window.removeEventListener(OPEN_INITIAL_SETUP_EVENT, openInitialSetup);
            window.removeEventListener(OPEN_JOIN_EXISTING_PARISH_EVENT, openJoinExistingParish);
            window.removeEventListener(OPEN_ADD_PARISH_EVENT, openAddParish);
        };
    }, []);

    useEffect(() => {
        if (isParishRequired) {
            setAddParishOpen(true);
            return;
        }

        setAddParishOpen(false);
    }, [isParishRequired]);

    if (isLoading) {
        return (
            <Container>
                <Loading />
            </Container>
        );
    }

    if (isError) {
        return (
            <Container>
                <div className={styles.errorContainer}>
                    <ErrorActionState onClick={refetch} />
                </div>
            </Container>
        );
    }

    return (
        <Container>
            <>
                <InitialSetupModal
                    open={Boolean(user && isInitialSetupRequired && isInitialSetupOpen)}
                    onClose={() => {
                        if (user && isInitialSetupRequired) {
                            specifiedSetItem(INITIAL_SETUP_AUTO_OPEN_DISMISSED_STORAGE_KEY, true);
                        }

                        setInitialSetupOpen(false);
                    }}
                />
                <InitialSetupChoiceModal
                    open={Boolean(user && isInitialSetupRequired && isInitialSetupChoiceOpen)}
                    onClose={() => {
                        if (user && isInitialSetupRequired) {
                            specifiedSetItem(INITIAL_SETUP_AUTO_OPEN_DISMISSED_STORAGE_KEY, true);
                        }

                        setInitialSetupChoiceOpen(false);
                    }}
                    onJoinExisting={() => {
                        setInitialSetupChoiceOpen(false);
                        setJoinExistingParishOpen(true);
                    }}
                    onConfigureOwn={() => {
                        setInitialSetupChoiceOpen(false);
                        setInitialSetupOpen(true);
                    }}
                />
                <JoinExistingParishModal
                    open={Boolean(user && isJoinExistingParishOpen)}
                    onClose={() => { setJoinExistingParishOpen(false); }}
                    onJoined={() => {
                        specifiedRemoveItem(INITIAL_SETUP_AUTO_OPEN_DISMISSED_STORAGE_KEY);
                        setInitialSetupOpen(false);
                        setInitialSetupChoiceOpen(false);
                    }}
                />
                {isParishRequired && (
                    <AddEntityProvider key={isAddParishOpen ? 'parish-open' : 'parish-closed'} isParishCreation>
                        <AddEntity
                            open={isAddParishOpen}
                            onClose={() => { setAddParishOpen(false); }}
                            isParishCreation
                        />
                    </AddEntityProvider>
                )}
                {(isInitialSetupRequired || isParishRequired) ? <NotConfiguredEmptyState /> : <Outlet />}
            </>
        </Container>
    );
};

export default Dashboard;
